import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Collapsible JSON viewer. Renders large API responses without choking the
 * UI thread on stringify of mega payloads.
 *
 * Each node knows how to render itself based on its JS type. Objects and
 * arrays start collapsed when they exceed `autoExpandSize`.
 */
export function JsonTree({
  value,
  rootName,
  autoExpandSize = 20,
}: {
  value: unknown;
  rootName?: string;
  autoExpandSize?: number;
}) {
  return (
    <div className="font-mono text-2xs leading-relaxed text-fg-secondary">
      <Node value={value} name={rootName} depth={0} autoExpandSize={autoExpandSize} />
    </div>
  );
}

function Node({
  value,
  name,
  depth,
  autoExpandSize,
  isLast = true,
}: {
  value: unknown;
  name?: string;
  depth: number;
  autoExpandSize: number;
  isLast?: boolean;
}) {
  if (value === null) return <Leaf name={name} type="null" display="null" isLast={isLast} />;
  if (value === undefined)
    return <Leaf name={name} type="undefined" display="undefined" isLast={isLast} />;
  const t = typeof value;
  if (t === 'string')
    return (
      <Leaf
        name={name}
        type="string"
        display={`"${(value as string).length > 200 ? (value as string).slice(0, 200) + '…' : value}"`}
        isLast={isLast}
      />
    );
  if (t === 'number' || t === 'boolean' || t === 'bigint')
    return <Leaf name={name} type={t} display={String(value)} isLast={isLast} />;
  if (Array.isArray(value)) {
    return (
      <Collapsible
        name={name}
        open={(value.length <= autoExpandSize && depth < 2)}
        openBracket="["
        closeBracket="]"
        summary={`Array(${value.length})`}
        isLast={isLast}
      >
        {value.map((item, i) => (
          <Node
            key={i}
            value={item}
            name={String(i)}
            depth={depth + 1}
            autoExpandSize={autoExpandSize}
            isLast={i === value.length - 1}
          />
        ))}
      </Collapsible>
    );
  }
  if (t === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj);
    return (
      <Collapsible
        name={name}
        open={(keys.length <= autoExpandSize && depth < 2)}
        openBracket="{"
        closeBracket="}"
        summary={`Object(${keys.length})`}
        isLast={isLast}
      >
        {keys.map((k, i) => (
          <Node
            key={k}
            value={obj[k]}
            name={k}
            depth={depth + 1}
            autoExpandSize={autoExpandSize}
            isLast={i === keys.length - 1}
          />
        ))}
      </Collapsible>
    );
  }
  return <Leaf name={name} type="unknown" display={String(value)} isLast={isLast} />;
}

function Leaf({
  name,
  type,
  display,
  isLast,
}: {
  name?: string;
  type: string;
  display: string;
  isLast: boolean;
}) {
  const valueClass = colorForType(type);
  return (
    <div className="pl-4">
      {name !== undefined && (
        <>
          <span className="text-fg">{quotedKey(name)}</span>
          <span className="text-fg-muted">: </span>
        </>
      )}
      <span className={valueClass}>{display}</span>
      {!isLast && <span className="text-fg-muted">,</span>}
    </div>
  );
}

function Collapsible({
  name,
  open: defaultOpen,
  openBracket,
  closeBracket,
  summary,
  isLast,
  children,
}: {
  name?: string;
  open: boolean;
  openBracket: string;
  closeBracket: string;
  summary: string;
  isLast: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="pl-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-0.5 hover:text-fg transition-colors"
      >
        <ChevronRight
          size={10}
          className={cn('text-fg-muted transition-transform', open && 'rotate-90')}
        />
        {name !== undefined && (
          <>
            <span className="text-fg">{quotedKey(name)}</span>
            <span className="text-fg-muted">: </span>
          </>
        )}
        <span className="text-fg-muted">{openBracket}</span>
        {!open && <span className="text-fg-muted ml-1">{summary}</span>}
      </button>
      {open && <div className="border-l border-border-subtle ml-1">{children}</div>}
      <div className="pl-0">
        <span className="text-fg-muted">{closeBracket}</span>
        {!isLast && <span className="text-fg-muted">,</span>}
      </div>
    </div>
  );
}

function quotedKey(name: string): string {
  // Only quote keys that aren't valid JS identifiers
  if (/^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(name)) return name;
  return `"${name}"`;
}

function colorForType(type: string): string {
  switch (type) {
    case 'string':
      return 'text-method-get'; // emerald-ish
    case 'number':
    case 'bigint':
      return 'text-method-put'; // amber
    case 'boolean':
      return 'text-method-patch'; // purple
    case 'null':
    case 'undefined':
      return 'text-fg-muted italic';
    default:
      return 'text-fg-secondary';
  }
}
